import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, DollarSign, TrendingUp, TrendingDown, Stethoscope } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { reportData } from '../data/mockData';

const PageContainer = styled.div`
  flex: 1;
  background: ${({ theme }) => theme.colors.background};
  padding-bottom: 80px;
  overflow-y: auto;
`;

const HeaderSection = styled.div`
  background: ${({ theme }) => theme.colors.white};
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const BackButton = styled.button`
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: all 0.3s ease;

  svg {
    width: 24px;
    height: 24px;
    color: ${({ theme }) => theme.colors.text};
  }

  &:hover {
    background: ${({ theme }) => theme.colors.gray};
  }
`;

const Title = styled.h1`
  font-size: 20px;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
`;

const Content = styled.div`
  padding: 20px;
`;

const TotalCard = styled.div`
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary} 0%, ${({ theme }) => theme.colors.primaryDark} 100%);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
  color: white;
`;

const TotalLabel = styled.div`
  font-size: 14px;
  opacity: 0.9;
  margin-bottom: 8px;
`;

const TotalValue = styled.div`
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 8px;
`;

const TotalChange = styled.div`
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 6px;
  opacity: 0.9;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const FilterTabs = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
  overflow-x: auto;
  padding-bottom: 8px;

  &::-webkit-scrollbar {
    height: 4px;
  }
`;

const FilterTab = styled.button`
  background: ${({ $active, theme }) => 
    $active ? theme.colors.primary : '#F0F0F0'};
  color: ${({ $active, theme }) => 
    $active ? theme.colors.white : theme.colors.text};
  border: none;
  border-radius: 20px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.3s ease;

  &:hover {
    background: ${({ $active, theme }) => 
      $active ? theme.colors.primaryDark : '#E0E0E0'};
  }
`;

const Section = styled.section`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h2`
  font-size: 16px;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 16px 0;
`;

const ChartCard = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 24px;
`;

const LegendContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 16px;
  justify-content: center;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
`;

const LegendColor = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 3px;
  background: ${({ color }) => color};
`;

const ServicesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ServiceCard = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  gap: 14px;
`;

const ServiceIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${({ color }) => color}20;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 24px;
    height: 24px;
    color: ${({ color }) => color};
  }
`;

const ServiceInfo = styled.div`
  flex: 1;
`;

const ServiceName = styled.h3`
  font-size: 15px;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 4px 0;
`;

const ServiceDetails = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
`;

const ServiceValue = styled.div`
  text-align: right;
`;

const ServiceAmount = styled.div`
  font-size: 16px;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ theme }) => theme.colors.primary};
`;

const ServiceChange = styled.div`
  font-size: 11px;
  color: ${({ $positive, theme }) => $positive ? theme.colors.successText : theme.colors.dangerText};
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  margin-top: 2px;

  svg {
    width: 12px;
    height: 12px;
  }
`;

const MonthlyTable = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  padding: 12px 16px;
  background: ${({ theme }) => theme.colors.gray};
  font-size: 12px;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  padding: 14px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  font-size: 14px;

  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.span`
  color: ${({ theme }) => theme.colors.text};

  &:last-child {
    text-align: right;
    font-weight: ${({ theme }) => theme.fontWeights.semibold};
    color: ${({ $positive, theme }) => $positive ? theme.colors.successText : theme.colors.dangerText};
  }
`;

const ReporteIngresos = () => {
  const navigate = useNavigate();
  const [selectedPeriod, setSelectedPeriod] = useState('Este mes');

  // Calcular total de ingresos
  const totalRevenue = reportData.serviceRevenue.reduce((sum, item) => sum + item.value, 0);

  // Datos detallados de servicios
  const servicesDetails = [
    { 
      name: 'Consultas', 
      count: 245, 
      amount: '$12,500', 
      change: '+15%', 
      positive: true,
      color: '#33A9FF' 
    },
    { 
      name: 'Exámenes', 
      count: 128, 
      amount: '$8,200', 
      change: '+8%', 
      positive: true,
      color: '#5CC8FF' 
    },
    { 
      name: 'Procedimientos', 
      count: 67, 
      amount: '$15,800', 
      change: '+22%', 
      positive: true,
      color: '#33D69F' 
    },
    { 
      name: 'Vacunación', 
      count: 312, 
      amount: '$4,680', 
      change: '-3%', 
      positive: false,
      color: '#FFB547' 
    },
    { 
      name: 'Terapias', 
      count: 89, 
      amount: '$6,230', 
      change: '+5%', 
      positive: true,
      color: '#FF6B9D' 
    },
  ];

  // Datos mensuales
  const monthlyData = [
    { month: 'Diciembre', revenue: '$47,410', change: '+12%', positive: true },
    { month: 'Noviembre', revenue: '$42,350', change: '+8%', positive: true },
    { month: 'Octubre', revenue: '$39,200', change: '+5%', positive: true },
    { month: 'Septiembre', revenue: '$37,100', change: '-2%', positive: false },
    { month: 'Agosto', revenue: '$37,850', change: '+10%', positive: true },
    { month: 'Julio', revenue: '$34,400', change: '+7%', positive: true },
  ];

  return (
    <PageContainer>
      <HeaderSection>
        <BackButton onClick={() => navigate('/reportes')}>
          <ArrowLeft />
        </BackButton>
        <Title>Detalle de Ingresos</Title>
      </HeaderSection>

      <Content>
        <TotalCard>
          <TotalLabel>Ingresos Totales del Período</TotalLabel>
          <TotalValue>$47,410</TotalValue>
          <TotalChange>
            <TrendingUp />
            +12% comparado con el mes anterior
          </TotalChange>
        </TotalCard>

        <FilterTabs>
          {['Hoy', 'Esta semana', 'Este mes', 'Este año'].map((period) => (
            <FilterTab
              key={period}
              $active={selectedPeriod === period}
              onClick={() => setSelectedPeriod(period)}
            >
              {period}
            </FilterTab>
          ))}
        </FilterTabs>

        <Section>
          <SectionTitle>Distribución por Servicio</SectionTitle>
          <ChartCard>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={reportData.serviceRevenue}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {reportData.serviceRevenue.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <LegendContainer>
              {reportData.serviceRevenue.map((item, index) => (
                <LegendItem key={index}>
                  <LegendColor color={item.color} />
                  <span>{item.name}: ${item.value.toLocaleString()}</span>
                </LegendItem>
              ))}
            </LegendContainer>
          </ChartCard>
        </Section>

        <Section>
          <SectionTitle>Desglose por Servicio</SectionTitle>
          <ServicesList>
            {servicesDetails.map((service, index) => (
              <ServiceCard key={index}>
                <ServiceIcon color={service.color}>
                  <Stethoscope />
                </ServiceIcon>
                <ServiceInfo>
                  <ServiceName>{service.name}</ServiceName>
                  <ServiceDetails>{service.count} atenciones</ServiceDetails>
                </ServiceInfo>
                <ServiceValue>
                  <ServiceAmount>{service.amount}</ServiceAmount>
                  <ServiceChange $positive={service.positive}>
                    {service.positive ? <TrendingUp /> : <TrendingDown />}
                    {service.change}
                  </ServiceChange>
                </ServiceValue>
              </ServiceCard>
            ))}
          </ServicesList>
        </Section>

        <Section>
          <SectionTitle>Histórico Mensual</SectionTitle>
          <MonthlyTable>
            <TableHeader>
              <span>Mes</span>
              <span>Ingresos</span>
              <span style={{ textAlign: 'right' }}>Variación</span>
            </TableHeader>
            {monthlyData.map((item, index) => (
              <TableRow key={index}>
                <TableCell>{item.month}</TableCell>
                <TableCell>{item.revenue}</TableCell>
                <TableCell $positive={item.positive}>{item.change}</TableCell>
              </TableRow>
            ))}
          </MonthlyTable>
        </Section>
      </Content>
    </PageContainer>
  );
};

export default ReporteIngresos;
